import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ArrowLeft, ChevronDown, Images, Loader2, Music, Plus, Sparkles, Upload } from 'lucide-react';
import './AssetDetailPage.css';
import './AssetsPage.css';
import { changeOrgAssetCameraAngle, getOrgAssets, magicSelectOrgAsset, patchOrgAsset, restoreOrgAssetVersion, updateOrgAsset, uploadOrgAsset } from './lib/api';
import { useAuthContext } from './components/AuthProvider';
import {
  useOrgAssetsStore,
  useBackgroundTasksStore,
  TASK_TYPES,
  useOrgAssetDetail,
  fetchOrgAssetDetailIfStale,
  useOrgAssetDetailsStore,
} from './stores';
import OrgAssetCreateView from './components/OrgAssetCreateView';
import ImageEditingWorkspace from './components/editor/ImageEditingWorkspace';

function AssetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const updateOrgAssetInStore = useOrgAssetsStore((state) => state.updateOrgAsset);
  const addOrgAsset = useOrgAssetsStore((state) => state.addOrgAsset);
  const orgAssets = useOrgAssetsStore((state) => state.orgAssets);
  const setOrgAssets = useOrgAssetsStore((state) => state.setOrgAssets);

  const addTask = useBackgroundTasksStore((state) => state.addTask);
  const completeTask = useBackgroundTasksStore((state) => state.completeTask);
  const failTask = useBackgroundTasksStore((state) => state.failTask);

  const setDetailEntry = useOrgAssetDetailsStore((s) => s.setEntry);
  const { data: asset, hasCache: hasDetailCache } = useOrgAssetDetail(assetId, user?.id);

  const [subAssets, setSubAssets] = useState([]);
  const [showSubAssetCreate, setShowSubAssetCreate] = useState(false);
  const [isUploadingSub, setIsUploadingSub] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [error, setError] = useState(null);
  const subFileInputRef = useRef(null);
  const committedNameRef = useRef('');
  const committedDescriptionRef = useRef('');

  const isLoading = !hasDetailCache;
  const loadingAssetPreview = orgAssets.find((orgAsset) => String(orgAsset.id) === String(assetId));

  const setAsset = useCallback(
    (next) => {
      if (next) setDetailEntry(assetId, next);
    },
    [assetId, setDetailEntry]
  );

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Fetch (or revalidate) the asset. Cached entries render instantly while this runs in the background.
  useEffect(() => {
    if (!assetId) return;
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        await fetchOrgAssetDetailIfStale({ id: assetId, userId: user?.id });
      } catch (err) {
        if (!cancelled) setError('Failed to load asset');
      }
    })();
    return () => { cancelled = true; };
  }, [assetId, user?.id]);

  // Seed sub-asset list once per asset id (don't clobber local mutations during background revalidation).
  const seededForIdRef = useRef(null);
  useEffect(() => {
    if (!asset || seededForIdRef.current === assetId) return;
    seededForIdRef.current = assetId;
    setSubAssets(asset.subAssets || []);
    setNameDraft(asset.name || '');
    setDescriptionDraft(asset.prompt || '');
    committedNameRef.current = asset.name || '';
    committedDescriptionRef.current = asset.prompt || '';
  }, [asset, assetId]);

  // Keep draft in sync when server name changes (e.g. revalidation) and user isn't mid-edit.
  useEffect(() => {
    const serverName = asset?.name || '';
    if (serverName && serverName !== committedNameRef.current && nameDraft === committedNameRef.current) {
      setNameDraft(serverName);
      committedNameRef.current = serverName;
    }
  }, [asset?.name, nameDraft]);

  useEffect(() => {
    const serverDescription = asset?.prompt || '';
    if (serverDescription !== committedDescriptionRef.current && descriptionDraft === committedDescriptionRef.current) {
      setDescriptionDraft(serverDescription);
      committedDescriptionRef.current = serverDescription;
    }
  }, [asset?.prompt, descriptionDraft]);

  const commitNameChange = useCallback(async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === committedNameRef.current) {
      if (!trimmed) setNameDraft(committedNameRef.current);
      return;
    }
    const prev = committedNameRef.current;
    committedNameRef.current = trimmed;
    updateOrgAssetInStore(assetId, { name: trimmed });
    if (asset) setAsset({ ...asset, name: trimmed });
    try {
      await patchOrgAsset(assetId, { name: trimmed });
    } catch (err) {
      committedNameRef.current = prev;
      setNameDraft(prev);
      updateOrgAssetInStore(assetId, { name: prev });
      if (asset) setAsset({ ...asset, name: prev });
      setError('Failed to rename asset');
    }
  }, [assetId, asset, nameDraft, setAsset, updateOrgAssetInStore]);

  const commitDescriptionChange = useCallback(async () => {
    const trimmed = descriptionDraft.trim();
    if (trimmed === committedDescriptionRef.current || isSavingDescription) return;
    const previous = committedDescriptionRef.current;
    setIsSavingDescription(true);
    setError(null);
    committedDescriptionRef.current = trimmed;
    updateOrgAssetInStore(assetId, { prompt: trimmed || null });
    if (asset) setAsset({ ...asset, prompt: trimmed || null });
    try {
      const response = await patchOrgAsset(assetId, { prompt: trimmed || null });
      const saved = response.asset?.prompt || '';
      committedDescriptionRef.current = saved;
      setDescriptionDraft(saved);
      if (response.asset) {
        setAsset(response.asset);
        updateOrgAssetInStore(assetId, { prompt: response.asset.prompt });
      }
    } catch (err) {
      committedDescriptionRef.current = previous;
      setDescriptionDraft(previous);
      updateOrgAssetInStore(assetId, { prompt: previous || null });
      if (asset) setAsset({ ...asset, prompt: previous || null });
      setError('Failed to save description');
    } finally {
      setIsSavingDescription(false);
    }
  }, [asset, assetId, descriptionDraft, isSavingDescription, setAsset, updateOrgAssetInStore]);

  useEffect(() => {
    if (orgAssets.length > 0) return undefined;
    let cancelled = false;
    const loadOrgAssets = async () => {
      try {
        const resp = await getOrgAssets();
        if (!cancelled) setOrgAssets(resp.assets || []);
      } catch (err) {
        console.warn('[AssetDetailPage] Failed to load global assets for sticker picker:', err);
      }
    };
    loadOrgAssets();
    return () => {
      cancelled = true;
    };
  }, [orgAssets.length, setOrgAssets]);

  const buildImageEditTask = useCallback((kind, prompt = '') => {
    const taskId = `org-asset-${kind}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    addTask({
      id: taskId,
      type: TASK_TYPES.ORG_ASSET_EDIT,
      assetId,
      prompt,
      createdAt: Date.now(),
    });
    return taskId;
  }, [addTask, assetId]);

  const handleGenerateImagePreview = useCallback(async ({ prompt, files, referenceImageUrls, model }) => {
    const taskId = buildImageEditTask('preview', prompt);
    try {
      const resp = await updateOrgAsset(assetId, prompt, files, {
        model,
        preview: true,
        referenceImageUrls,
      });
      completeTask(taskId, { assetId, assetUrl: resp.asset?.imageUrl, assetName: resp.asset?.name });
      return resp.asset;
    } catch (err) {
      failTask(taskId, err);
      throw err;
    }
  }, [assetId, buildImageEditTask, completeTask, failTask]);

  const handleCommitImagePreview = useCallback(async ({ prompt, pendingUrl, model }) => {
    const taskId = buildImageEditTask('commit-preview', prompt);
    try {
      const resp = await updateOrgAsset(assetId, prompt, [], {
        commitUrl: pendingUrl,
        model,
      });
      if (resp.asset && isMountedRef.current) {
        setAsset(resp.asset);
        updateOrgAssetInStore(assetId, {
          imageUrl: resp.asset.imageUrl,
          prompt: resp.asset.prompt,
          name: resp.asset.name,
        });
      }
      completeTask(taskId, { assetId, assetUrl: resp.asset?.imageUrl, assetName: resp.asset?.name });
      return resp.asset;
    } catch (err) {
      failTask(taskId, err);
      throw err;
    }
  }, [assetId, buildImageEditTask, completeTask, failTask, updateOrgAssetInStore]);

  const handleCommitImageUpload = useCallback(async ({ prompt, file, model }) => {
    const taskId = buildImageEditTask('commit-upload', prompt);
    try {
      const resp = await updateOrgAsset(assetId, prompt, [file], {
        commitUpload: true,
        model,
      });
      if (resp.asset && isMountedRef.current) {
        setAsset(resp.asset);
        updateOrgAssetInStore(assetId, {
          imageUrl: resp.asset.imageUrl,
          prompt: resp.asset.prompt,
          name: resp.asset.name,
        });
      }
      completeTask(taskId, { assetId, assetUrl: resp.asset?.imageUrl, assetName: resp.asset?.name });
      return resp.asset;
    } catch (err) {
      failTask(taskId, err);
      throw err;
    }
  }, [assetId, buildImageEditTask, completeTask, failTask, updateOrgAssetInStore]);

  const handleCameraAnglePreview = useCallback(async ({ horizontalAngle, verticalAngle, zoom, model }) => {
    const taskId = buildImageEditTask('camera-angle', `Camera angle ${horizontalAngle}/${verticalAngle}/${zoom}`);
    try {
      const resp = await changeOrgAssetCameraAngle(assetId, {
        horizontalAngle,
        verticalAngle,
        zoom,
        preview: true,
        model,
      });
      completeTask(taskId, { assetId, assetUrl: resp.asset?.imageUrl, assetName: resp.asset?.name });
      return resp.asset;
    } catch (err) {
      failTask(taskId, err);
      throw err;
    }
  }, [assetId, buildImageEditTask, completeTask, failTask]);

  const handleRestoreVersion = useCallback(async (versionNumber) => {
    const resp = await restoreOrgAssetVersion(assetId, versionNumber);
    if (resp.asset && isMountedRef.current) {
      setAsset(resp.asset);
      updateOrgAssetInStore(assetId, {
        imageUrl: resp.asset.imageUrl,
        prompt: resp.asset.prompt,
        name: resp.asset.name,
        versions: resp.asset.versions,
        activeVersionNumber: resp.asset.activeVersionNumber,
      });
    }
    return resp.asset;
  }, [assetId, updateOrgAssetInStore]);

  const handleMagicSelect = useCallback(async ({ box, imageUrl }) => {
    return magicSelectOrgAsset(assetId, box, imageUrl);
  }, [assetId]);

  const handleSubAssetCreated = (subAsset) => {
    setSubAssets((prev) => [subAsset, ...prev]);
    addOrgAsset(subAsset);
    setShowSubAssetCreate(false);
    navigate(`/assets/${subAsset.id}`);
  };

  const handleSubAssetUpload = async (file) => {
    if (!file) return;
    try {
      setIsUploadingSub(true);
      setError(null);
      const resp = await uploadOrgAsset(file, { parentAssetId: assetId });
      if (resp.success && resp.asset) {
        handleSubAssetCreated(resp.asset);
      }
    } catch (err) {
      setError('Upload failed');
    } finally {
      setIsUploadingSub(false);
      if (subFileInputRef.current) subFileInputRef.current.value = '';
    }
  };

  const handleBack = () => {
    navigate(asset?.parentAssetId ? `/assets/${asset.parentAssetId}` : '/assets');
  };

  if (isLoading) {
    return (
      <div className="asset-detail-loading-shell" aria-busy="true" aria-label="Loading image editor">
        <aside className="asset-detail-loading-tools">
          <div className="asset-detail-loading-header">
            <button
              type="button"
              className="asset-detail-loading-back"
              onClick={() => navigate('/assets')}
              aria-label="Back to Images"
            >
              <ArrowLeft size={18} />
            </button>
            <span className="asset-detail-loading-title">
              {loadingAssetPreview?.name || 'Image'}
            </span>
          </div>

          <div className="asset-detail-loading-tool-list" aria-hidden="true">
            <div className="asset-detail-loading-section-label">Edit image</div>
            <div className="asset-detail-loading-tool-row">
              <span className="asset-detail-loading-tool-icon" />
              <span className="asset-detail-loading-line asset-detail-loading-line--medium" />
            </div>
            <div className="asset-detail-loading-tool-row">
              <span className="asset-detail-loading-tool-icon" />
              <span className="asset-detail-loading-line asset-detail-loading-line--short" />
            </div>
            <div className="asset-detail-loading-tool-row">
              <span className="asset-detail-loading-tool-icon" />
              <span className="asset-detail-loading-line asset-detail-loading-line--long" />
            </div>
          </div>
        </aside>

        <main className="asset-detail-loading-main">
          <div className="asset-detail-loading-canvas">
            {loadingAssetPreview?.imageUrl ? (
              <img
                src={loadingAssetPreview.imageUrl}
                alt=""
                className="asset-detail-loading-image"
              />
            ) : (
              <div className="asset-detail-loading-image-placeholder" aria-hidden="true">
                <Images size={32} />
              </div>
            )}
            <div className="asset-detail-loading-status" role="status" aria-live="polite">
              <Loader2 size={15} className="asset-detail-spinner" />
              <span>Preparing editor</span>
            </div>
          </div>

          <div className="asset-detail-loading-prompt" aria-hidden="true">
            <Sparkles size={16} />
            <span className="asset-detail-loading-line asset-detail-loading-line--prompt" />
            <span className="asset-detail-loading-send" />
          </div>
        </main>

        <aside className="asset-detail-loading-details" aria-hidden="true">
          <div className="asset-detail-loading-details-section">
            <div className="asset-detail-loading-section-label">Description</div>
            <div className="asset-detail-loading-text-block">
              <span className="asset-detail-loading-line asset-detail-loading-line--long" />
              <span className="asset-detail-loading-line asset-detail-loading-line--medium" />
              <span className="asset-detail-loading-line asset-detail-loading-line--short" />
            </div>
          </div>
          <div className="asset-detail-loading-details-section">
            <div className="asset-detail-loading-section-label">Variations</div>
            <div className="asset-detail-loading-variation">
              <span className="asset-detail-loading-variation-thumb" />
              <span className="asset-detail-loading-line asset-detail-loading-line--medium" />
            </div>
          </div>
        </aside>
      </div>
    );
  }

  if (error && !asset) {
    return (
      <div className="asset-detail-error asset-detail-error--fullscreen">
        <p>{error}</p>
        <Link to="/assets" className="asset-detail-back-link">← Back to Images</Link>
      </div>
    );
  }

  const assetMediaType = asset?.mediaType || asset?.media_type || 'image';
  const assetVideoUrl = asset?.videoUrl || asset?.video_url || null;
  const assetAudioUrl = asset?.audioUrl || asset?.audio_url || null;
  const assetHasMedia =
    (assetMediaType === 'image' && asset?.imageUrl) ||
    (assetMediaType === 'video' && assetVideoUrl) ||
    (assetMediaType === 'audio' && assetAudioUrl);

  if (!assetHasMedia) {
    return (
      <div className="asset-detail-error asset-detail-error--fullscreen">
        <Images size={48} />
        <p>No media yet</p>
        <Link to="/assets" className="asset-detail-back-link">← Back to Images</Link>
      </div>
    );
  }

  if (showSubAssetCreate) {
    return (
      <div className="asset-detail-sub-create-fullscreen">
        <OrgAssetCreateView
          parentAssetId={assetId}
          parentAsset={asset}
          onAssetGenerated={handleSubAssetCreated}
          onCancel={() => setShowSubAssetCreate(false)}
        />
      </div>
    );
  }

  const variationsPanel = !asset?.parentAssetId ? (
    <div className="asset-detail-editor-panel">
      <div className="asset-detail-editor-section">
        <div className="asset-detail-sidebar-header">
          <span className="asset-detail-sidebar-title">Variations</span>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className="asset-detail-add-sub-btn" disabled={isUploadingSub}>
                <Plus size={13} />
                {isUploadingSub ? 'Uploading…' : 'Add'}
                <ChevronDown size={12} />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="assets-page-dropdown" sideOffset={6} align="end">
                <DropdownMenu.Item
                  className="assets-page-dropdown-item"
                  onSelect={() => setShowSubAssetCreate(true)}
                >
                  <Sparkles size={14} />
                  Generate with AI
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className="assets-page-dropdown-item"
                  onSelect={() => subFileInputRef.current?.click()}
                >
                  <Upload size={14} />
                  Upload image
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          <input
            ref={subFileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => handleSubAssetUpload(e.target.files?.[0])}
          />
        </div>
        {subAssets.length === 0 && <p className="asset-detail-sub-empty">No variations yet.</p>}
        {subAssets.length > 0 && (
          <div className="asset-detail-sub-list">
            {subAssets.map((sub) => (
              <div
                key={sub.id}
                className="asset-detail-sub-card"
                onClick={() => navigate(`/assets/${sub.id}`)}
                onMouseEnter={() => fetchOrgAssetDetailIfStale({ id: sub.id, userId: user?.id })}
                onFocus={() => fetchOrgAssetDetailIfStale({ id: sub.id, userId: user?.id })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/assets/${sub.id}`)}
              >
                <div className="asset-detail-sub-image-wrapper">
                  {sub.imageUrl ? (
                    <img src={sub.imageUrl} alt={sub.name} className="asset-detail-sub-image" />
                  ) : (
                    <div className="asset-detail-sub-placeholder">
                      <Images size={20} />
                    </div>
                  )}
                </div>
                <span className="asset-detail-sub-name">{sub.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  const descriptionPanel = (
    <div className="asset-detail-editor-panel asset-detail-description-panel">
      <div className="asset-detail-editor-section">
        <div className="asset-detail-sidebar-header">
          <label className="asset-detail-sidebar-title" htmlFor="asset-description">Description</label>
          <button
            type="button"
            className="asset-detail-add-sub-btn"
            onClick={commitDescriptionChange}
            disabled={isSavingDescription || descriptionDraft.trim() === committedDescriptionRef.current}
          >
            {isSavingDescription ? 'Saving…' : 'Save'}
          </button>
        </div>
        <textarea
          id="asset-description"
          className="asset-detail-description-input"
          value={descriptionDraft}
          onChange={(event) => setDescriptionDraft(event.target.value)}
          maxLength={5000}
          placeholder="Add a description…"
        />
      </div>
    </div>
  );

  const assetSidePanel = <>{descriptionPanel}{variationsPanel}</>;

  // For non-image assets, ImageEditingWorkspace doesn't apply (no image to edit).
  // Render a fullscreen viewer that mirrors the chrome of the image variant —
  // header with back + editable title, centered media player, optional variations sidebar.
  if (assetMediaType !== 'image') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          background: 'var(--color-bg-root)',
          color: 'var(--color-text-primary)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 20px',
            borderBottom: '1px solid var(--color-border-input)',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            className="config-back-button"
            onClick={handleBack}
            aria-label="Back to assets"
          >
            <ArrowLeft size={20} />
          </button>
          <input
            type="text"
            className="asset-detail-editor-name-input"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitNameChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              } else if (e.key === 'Escape') {
                setNameDraft(committedNameRef.current);
                e.currentTarget.blur();
              }
            }}
            placeholder="Asset name"
            aria-label="Asset name"
            title={nameDraft}
            style={{ flex: 1, minWidth: 0 }}
          />
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
              padding: 32,
              minWidth: 0,
            }}
          >
            {assetMediaType === 'video' ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  maxWidth: 960,
                  background: '#000',
                  borderRadius: 12,
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4)',
                }}
              >
                <video
                  src={assetVideoUrl}
                  controls
                  playsInline
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 24,
                  padding: 48,
                  width: '100%',
                  maxWidth: 560,
                  background: '#1a1a1a',
                  borderRadius: 12,
                  border: '1px solid var(--color-border-input)',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
                }}
              >
                <Music size={96} strokeWidth={1.0} style={{ color: '#888' }} />
                <audio src={assetAudioUrl} controls preload="metadata" style={{ width: '100%' }} />
              </div>
            )}
          </div>
          <aside
            style={{
              width: 320,
              flexShrink: 0,
              borderLeft: '1px solid var(--color-border-input)',
              overflowY: 'auto',
              background: 'var(--color-bg-sidebar, var(--color-bg-root))',
            }}
          >
            {assetSidePanel}
          </aside>
        </div>
      </div>
    );
  }

  return (
    <ImageEditingWorkspace
      imageId={asset.id}
      imageUrl={asset.imageUrl}
      imageName={asset.name}
      allAssets={[
        ...orgAssets.map((orgAsset) => (
          orgAsset.id === asset.id ? { ...orgAsset, subAssets } : orgAsset
        )),
        ...(orgAssets.some((orgAsset) => orgAsset.id === asset.id) ? [] : [{ ...asset, subAssets }]),
      ]}
      assetMentionScope="global"
      variant="fullscreen"
      title={(
        <input
          type="text"
          className="asset-detail-editor-name-input"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commitNameChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              setNameDraft(committedNameRef.current);
              e.currentTarget.blur();
            }
          }}
          placeholder="Asset name"
          aria-label="Asset name"
          title={nameDraft}
        />
      )}
      sidePanel={assetSidePanel}
      versions={asset.versions || []}
      activeVersionNumber={asset.activeVersionNumber ?? 0}
      onRestoreVersion={handleRestoreVersion}
      onCameraAnglePreview={handleCameraAnglePreview}
      onMagicSelect={handleMagicSelect}
      onBack={handleBack}
      onGeneratePreview={handleGenerateImagePreview}
      onCommitPreview={handleCommitImagePreview}
      onCommitUpload={handleCommitImageUpload}
      onSaveComplete={handleBack}
      onCommitted={(updatedAsset) => {
        if (!updatedAsset) return;
        setAsset(updatedAsset);
        updateOrgAssetInStore(assetId, {
          imageUrl: updatedAsset.imageUrl,
          prompt: updatedAsset.prompt,
          name: updatedAsset.name,
          versions: updatedAsset.versions,
          activeVersionNumber: updatedAsset.activeVersionNumber,
        });
      }}
      promptPlaceholder="Describe changes to this asset..."
    />
  );
}

export default AssetDetailPage;
